import axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { API_ENDPOINTS } from './endpoints';
import { dispatchGlobalError, dispatchGlobalSuccess } from '@/components/GlobalSnackbar';

// Development token for testing (set in .env.development)
const DEV_TOKEN = import.meta.env.VITE_DEV_AUTH_TOKEN;
const IS_DEV = import.meta.env.DEV;

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Create Axios instance
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1',
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
    // 'X-User-ID': "0f8e2751-b53b-42f7-9154-e699e8ca4d9b",
    // 'X-User-Type': "system_admin",
  },
});

// Helper to get current access token
const getAccessToken = (): string | null => {
  // redux-persist stores slices under persist:root
  const rootState = localStorage.getItem('persist:root');
  if (!rootState) return null;

  try {
    const root = JSON.parse(rootState) as { auth?: string | { token?: string | null } };
    const authState = typeof root.auth === 'string' ? JSON.parse(root.auth) : root.auth;
    return authState?.token ?? null;
  } catch (error) {
    console.error('[API] Failed to parse persisted auth state:', error);
    return null;
  }
};

// Helper to clear all auth state
const clearAuthState = (): void => {
  localStorage.removeItem('refresh_token');
  const rootState = localStorage.getItem('persist:root');
  if (!rootState) return;

  try {
    const root = JSON.parse(rootState) as Record<string, string>;
    const authState = typeof root.auth === 'string' ? JSON.parse(root.auth) : (root.auth ?? {});
    authState.user = null;
    authState.token = null;
    authState.isAuthenticated = false;
    root.auth = JSON.stringify(authState);
    localStorage.setItem('persist:root', JSON.stringify(root));
  } catch {
    // Fallback: remove entire persist state
    localStorage.removeItem('persist:root');
  }
};

// Helper to update access token in Redux persisted state
const updateAccessToken = (newToken: string): void => {
  const rootState = localStorage.getItem('persist:root');
  if (!rootState) return;

  try {
    const root = JSON.parse(rootState) as { auth?: string | { token?: string | null } };
    const authState = typeof root.auth === 'string' ? JSON.parse(root.auth) : (root.auth ?? {});
    authState.token = newToken;
    root.auth = JSON.stringify(authState);
    localStorage.setItem('persist:root', JSON.stringify(root));
  } catch (error) {
    console.error('[API] Failed to update persisted auth state:', error);
  }
};

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get auth token
    const token = getAccessToken();

    if (token) {
       config.headers.set('Authorization', `Bearer ${token}`);
    }

    // Attach MFA device trust token on login requests
    const url = config.url || '';
    if (url.includes('/auth/login')) {
      const deviceToken = localStorage.getItem('mfa_device_token');
      if (deviceToken) {
        config.headers.set('X-MFA-Device-Token', deviceToken);
      }
    }

    return config;
  },
  (error: AxiosError) => {
    console.error('[API] Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor with automatic token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const requestId = response.config.headers.get('X-Request-ID');
    const requestIdStr = typeof requestId === 'string' ? requestId : '';
    const duration = Date.now() - parseInt(requestIdStr.split('_')[1] || '0');

    if (duration > 3000) {
      console.warn(`[API] Slow request (${duration}ms):`, response.config.url);
    }

    console.log(`[API] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`, {
      requestId,
      duration: `${duration}ms`,
    });

    // Show success notification for mutation requests (not GET)
    const method = response.config.method?.toUpperCase();
    if (method && ['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
      // Skip for auth/token endpoints
      const url = response.config.url || '';
      if (!url.includes('/auth/')) {
        dispatchGlobalSuccess('Saved successfully.');
      }
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const requestId = originalRequest?.headers?.get('X-Request-ID');

    // Handle specific error cases
    if (error.response) {
      const status = error.response.status;
      const url = originalRequest?.url;

      console.error(`[API] ${originalRequest?.method?.toUpperCase()} ${url} - ${status}`, {
        requestId,
        error: error.response.data,
      });

      // Handle account lockout (423)
      if (status === 423) {
        const lockData = error.response.data as { detail?: string };
        return Promise.reject({
          message: lockData?.detail || 'Account is locked. Please contact your administrator.',
          status,
        });
      }

      // Handle authentication errors with automatic refresh
      if (status === 401 && !originalRequest._retry) {
        // Skip refresh for login/refresh endpoints
        if (url?.includes('/auth/login') || url?.includes('/auth/refresh')) {
          const loginError = error.response.data as { detail?: string };
          return Promise.reject({
            message: loginError?.detail || 'Invalid credentials',
            status,
          });
        }

        // Dev mode bypass
        if (IS_DEV && DEV_TOKEN) {
          return Promise.reject({
            message: 'Session expired. Please log in again.',
            status,
          });
        }

        // Try to refresh the token
        if (isRefreshing) {
          // Queue this request while refresh is in progress
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.set('Authorization', `Bearer ${token}`);
              return apiClient(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem('refresh_token');

        if (!refreshToken) {
          // No refresh token, redirect to login
          isRefreshing = false;
          clearAuthState();
          window.location.href = '/login';
          return Promise.reject({
            message: 'Session expired. Please log in again.',
            status,
          });
        }

        try {
          // Call refresh endpoint (uses raw axios to skip interceptors)
          const baseUrl = (apiClient.defaults.baseURL || '').replace(/\/$/, '');
          const response = await axios.post(
            `${baseUrl}/${API_ENDPOINTS.AUTH.REFRESH}`,
            { refresh_token: refreshToken },
            { headers: { 'Content-Type': 'application/json' } }
          );

          const { access_token, refresh_token: newRefreshToken } = response.data;

          // Update stored tokens (token rotation)
          updateAccessToken(access_token);
          if (newRefreshToken) {
            localStorage.setItem('refresh_token', newRefreshToken);
          }

          // Process queued requests
          processQueue(null, access_token);

          // Retry original request
          originalRequest.headers.set('Authorization', `Bearer ${access_token}`);
          return apiClient(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear auth and redirect
          processQueue(refreshError, null);
          clearAuthState();
          window.location.href = '/login';
          return Promise.reject({
            message: 'Session expired. Please log in again.',
            status,
          });
        } finally {
          isRefreshing = false;
        }
      }

      // Handle forbidden
      if (status === 403) {
        return Promise.reject({
          message: 'You do not have permission to perform this action.',
          status,
        });
      }

      // Handle not found
      if (status === 404) {
        return Promise.reject({
          message: 'Resource not found.',
          status,
        });
      }

      // Handle rate limiting
      if (status === 429) {
        return Promise.reject({
          message: 'Too many requests. Please try again later.',
          status,
        });
      }

      // Handle server errors
      if (status >= 500) {
        dispatchGlobalError('Server error. Please try again later.');
        return Promise.reject({
          message: 'Server error. Please try again later.',
          status,
          details: error.response.data,
        });
      }

      // Handle other client errors
      const errorData = error.response.data as { detail?: string; message?: string };
      const clientMessage = errorData?.detail || errorData?.message || 'Request failed';
      dispatchGlobalError(clientMessage);
      return Promise.reject({
        message: clientMessage,
        status,
        details: error.response.data,
      });
    }

    // Handle network errors
    if (error.request) {
      console.error('[API] Network error:', {
        requestId,
        message: error.message,
      });
      dispatchGlobalError('Network error. Please check your connection.');
      return Promise.reject({
        message: 'Network error. Please check your connection.',
        status: 0,
      });
    }

    // Handle other errors
    console.error('[API] Error:', error.message);
    return Promise.reject({
      message: error.message || 'An unexpected error occurred',
      status: 0,
    });
  }
);

export default apiClient;
